import { useSyncExternalStore } from 'react';

/**
 * Background report jobs, shared by every screen.
 *
 * A job is created when the user queues a report. The tray (mounted in the
 * main layout) polls the report logs, links the job to the log row the
 * scheduler creates, and flips it to `ready` when the log completes — so
 * users can keep working elsewhere while the report runs.
 */

/** failed / empty: the scheduler marked the run F (error) or N (no rows) */
export type ReportJobStatus = 'queued' | 'ready' | 'expired' | 'failed' | 'empty';

export interface ReportJob {
  id: string;
  repId: number;
  reportName: string;
  /** encrypted report id from the route, to link back to the report page */
  encId: string | null;
  submittedAt: number;
  /** log ids that already existed at submit time — never match those */
  knownLogIds: number[];
  logId: number | null;
  status: ReportJobStatus;
  pdfUrl: string | null;
  excelUrl: string | null;
  /** user has opened or dismissed the "ready" notice */
  seen: boolean;
}

export interface ReportViewerTarget {
  logId: number;
  reportName: string;
  pdfUrl: string | null;
  excelUrl: string | null;
}

interface State {
  userCode: string | null;
  jobs: ReportJob[];
  viewer: ReportViewerTarget | null;
}

const STORAGE_PREFIX = 'erps.reportJobs.v1.';
/** polling stops after this; a job that never completes is shown as expired */
export const JOB_EXPIRY_MS = 2 * 60 * 60 * 1000;
/** finished jobs drop off the tray after a day */
const READY_RETENTION_MS = 24 * 60 * 60 * 1000;

let state: State = { userCode: null, jobs: [], viewer: null };
const listeners = new Set<() => void>();

const persist = () => {
  if (!state.userCode) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + state.userCode, JSON.stringify(state.jobs));
  } catch {
    // storage full or blocked: jobs still live for this session
  }
};

const setState = (next: Partial<State>) => {
  state = { ...state, ...next };
  if ('jobs' in next) persist();
  listeners.forEach((listener) => listener());
};

const updateJobs = (update: (jobs: ReportJob[]) => ReportJob[]) =>
  setState({ jobs: update(state.jobs) });

export const reportJobs = {
  /** load the signed-in user's jobs; call when the user changes */
  init(userCode: string | null) {
    if (userCode === state.userCode) return;
    let jobs: ReportJob[] = [];
    if (userCode) {
      try {
        jobs = JSON.parse(localStorage.getItem(STORAGE_PREFIX + userCode) ?? '[]');
      } catch {
        jobs = [];
      }
    }
    const now = Date.now();
    jobs = jobs.filter(
      (job) => job.status === 'queued' || now - job.submittedAt < READY_RETENTION_MS
    );
    state = { userCode, jobs, viewer: null };
    persist();
    listeners.forEach((listener) => listener());
  },

  add(job: Omit<ReportJob, 'id' | 'logId' | 'status' | 'pdfUrl' | 'excelUrl' | 'seen'>) {
    const id = `${job.repId}-${job.submittedAt}-${Math.random().toString(36).slice(2, 7)}`;
    updateJobs((jobs) => [
      { ...job, id, logId: null, status: 'queued', pdfUrl: null, excelUrl: null, seen: false },
      ...jobs,
    ]);
    return id;
  },

  patch(id: string, patch: Partial<ReportJob>) {
    updateJobs((jobs) => jobs.map((job) => (job.id === id ? { ...job, ...patch } : job)));
  },

  remove(id: string) {
    updateJobs((jobs) => jobs.filter((job) => job.id !== id));
  },

  clearFinished() {
    updateJobs((jobs) => jobs.filter((job) => job.status === 'queued'));
  },

  markAllSeen() {
    if (state.jobs.every((job) => job.seen || job.status === 'queued')) return;
    updateJobs((jobs) =>
      jobs.map((job) => (job.status === 'queued' ? job : { ...job, seen: true }))
    );
  },

  openViewer(target: ReportViewerTarget) {
    updateJobs((jobs) =>
      jobs.map((job) => (job.logId === target.logId ? { ...job, seen: true } : job))
    );
    setState({ viewer: target });
  },

  closeViewer() {
    setState({ viewer: null });
  },

  getState: () => state,
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useReportJobs = <T>(select: (s: State) => T): T =>
  useSyncExternalStore(subscribe, () => select(state));
